// Các mảnh dựng chung của hộp hướng dẫn — dùng bởi CẢ hộp chung
// (`GuideDialog`) lẫn hộp riêng của trang lương (`SalaryGuideDialog`).
//
// Tách ra vì hai hộp bày cùng một loại nội dung theo hai bố cục khác nhau: một
// cột dọc và chia thẻ. Để mỗi hộp một bản chép tay thì sửa cách bày ở một bên
// là hai hộp lệch nhau, và không ai nhận ra cho tới khi mở hộp kia lên.

import type { GuideBlock, PageGuide } from "./types";

/** Danh sách gạch đầu dòng — dùng cho "trên màn có gì" và "cách trang chạy". */
export function GuideBulletList({ items }: { items: string[] }) {
  return (
    <ul className="list-disc space-y-1.5 pl-5 text-muted">
      {items.map((t) => (
        <li key={t} className="leading-relaxed">
          {t}
        </li>
      ))}
    </ul>
  );
}

/** Các khối việc theo nhóm nghề: bước đánh số, kèm hộp "hay gặp". */
export function GuideBlockList({ blocks }: { blocks: GuideBlock[] }) {
  return (
    <>
      {blocks.map((b) => (
        <section key={b.title}>
          <h3 className="mb-2 font-semibold">{b.title}</h3>
          <ol className="list-decimal space-y-1.5 pl-5">
            {b.steps.map((s) => (
              <li key={s} className="leading-relaxed">
                {s}
              </li>
            ))}
          </ol>
          {b.troubles && b.troubles.length > 0 && (
            <div className="mt-3 rounded-md border border-warning/30 bg-warning/5 p-3">
              <p className="mb-1 font-medium">Hay gặp</p>
              <ul className="list-disc space-y-1 pl-5 text-muted">
                {b.troubles.map((t) => (
                  <li key={t} className="leading-relaxed">
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      ))}
    </>
  );
}

/**
 * Sắc thái một nhãn trạng thái mang trên MÀN THẬT.
 *
 * Không phải trang trí: người đọc mở hướng dẫn ra là để đối chiếu với cái họ
 * đang nhìn, nên nhãn "Đã thu" ở đây phải xanh đúng như badge xanh trên màn.
 * Bảng hướng dẫn một màu, màn một màu là bắt họ tự dịch.
 */
export type StatusTone = "info" | "warning" | "success" | "danger";

const STATUS_TONE: Record<StatusTone, { bg: string; text: string; border: string }> = {
  info: { bg: "var(--color-info-bg)", text: "var(--color-info-text)", border: "var(--color-info)" },
  warning: {
    bg: "var(--color-warning-bg)",
    text: "var(--color-warning-text)",
    border: "var(--color-warning)",
  },
  success: {
    bg: "var(--color-success-bg)",
    text: "var(--color-success-text)",
    border: "var(--color-success)",
  },
  danger: {
    bg: "var(--color-danger-bg)",
    text: "var(--color-danger-text)",
    border: "var(--color-danger)",
  },
};

/**
 * Bảng nghĩa của các trạng thái.
 *
 * `labelWidth` rộng hẹp khác nhau giữa hai hộp vì nhãn trạng thái của trang
 * lương là những câu dài ("Bắt buộc nhập lý do thay đổi…"), còn trang khác
 * phần lớn là một hai chữ.
 *
 * `tone` là TUỲ CHỌN và mặc định vắng mặt: hộp chung và hộp trang lương giữ
 * nguyên cách bày cũ, không đổi một pixel. Chỉ hộp nào có nhãn tô màu trên màn
 * thật mới truyền vào.
 */
export function GuideStatusList({
  statuses,
  labelWidth = "sm:w-32",
  tone,
}: {
  statuses: NonNullable<PageGuide["statuses"]>;
  labelWidth?: string;
  tone?: ((label: string) => StatusTone | undefined) | undefined;
}) {
  return (
    <dl className="space-y-1.5">
      {statuses.map((s) => {
        const t = tone?.(s.label);
        const mau = t ? STATUS_TONE[t] : undefined;
        return (
          <div key={s.label} className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
            <dt className={`shrink-0 font-medium ${labelWidth}`}>
              {mau ? (
                <span
                  className="inline-block rounded border px-1.5 py-0.5 text-xs"
                  style={{ background: mau.bg, borderColor: mau.border, color: mau.text }}
                >
                  {s.label}
                </span>
              ) : (
                s.label
              )}
            </dt>
            <dd className="min-w-0 text-muted">{s.meaning}</dd>
          </div>
        );
      })}
    </dl>
  );
}
